import { useQuery } from '@tanstack/react-query';

import { getVariableManifest } from '@/lib/api/generated/api';
import type { VariableManifest } from '@/lib/api/generated/model';

import { triggerKeys } from '../queryKeys';

/**
 * A driver's static template-variable manifest (spec 074, US3, FR-009) —
 * fetched once per template-field editing session and walked entirely
 * client-side by the autocomplete component (`SignalTemplateField`); no
 * further network call happens per keystroke or per suggestion
 * (contracts/template-variable-manifest-api.md's frontend contract).
 *
 * Same hand-written-`queryFn` convention as `useTriggerProfile`/
 * `useTriggerProfiles` — calls the generated raw fetcher directly.
 */
export function useVariableManifest(workspaceId: string, driverType: string) {
  const query = useQuery({
    queryKey: triggerKeys.manifest(workspaceId, driverType),
    queryFn: async (): Promise<VariableManifest> => {
      const response = await getVariableManifest(workspaceId, driverType);
      if (response.status !== 200) {
        throw new Error('The variable manifest could not be loaded.');
      }
      return response.data;
    },
    // Static content (research.md D-MAN1) — never invalidated, and safe to
    // treat as permanently fresh once fetched for a given driver type.
    staleTime: Infinity,
    retry: false,
  });

  return {
    manifest: query.data,
    isPending: query.isPending,
    loadFailed: query.isError,
  };
}
